import * as React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { skipToken } from "@tanstack/react-query";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { ProductsTable } from "@v2/features/products/components/ProductsTable";
import { ShopifyProductImportPrompt } from "@v2/features/products/components/ShopifyProductImportPrompt";
import { ProductsSetup } from "@v2/features/products/ProductsSetup";
import { productRouter } from "@v2/trpc/routes/product";

function ProductsTableSkeleton() {
  return (
    <>
      <PageHeader
        title={<Skeleton className="h-8 w-32" />}
        actions={<Skeleton className="h-9 w-28" />}
      />
      <div className="flex w-full flex-col gap-y-6 py-6">
        <Skeleton className="h-10 w-full" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 py-3 border-b border-border"
            >
              <Skeleton className="h-12 w-12 rounded" />
              <div className="flex flex-col gap-1 flex-1">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export function ProductsDashboard() {
  const { project } = useCurrentProjectContext();
  const shopifyData = project?.integrations?.shopify?.store;
  const projectId = project?.id;

  const { data: products, isLoading: isLoadingProducts } =
    productRouter.list.useQuery(
      projectId
        ? {
            projectId,
            limit: 1,
            writeThroughProductsSync: true,
          }
        : skipToken,
    );

  const shouldShowProductsSetup = Boolean(
    !shopifyData && (!products || products.products.length === 0),
  );

  if (isLoadingProducts) {
    return (
      <div className="relative flex flex-col w-full h-full items-center">
        <ProductsTableSkeleton />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col w-full h-full items-center">
      {shouldShowProductsSetup ? <ProductsSetup /> : <ProductsTable />}
      <ShopifyProductImportPrompt />
    </div>
  );
}
